import React, { useMemo } from "react";
import { FeatherInfo, FeatherArrowDown, FeatherArrowUp } from "@subframe/core";
import { PlSummaryCardSkeleton } from "../shared/PlSummaryCardSkeleton";

const MIN_BAR_PERCENT = 8; // minimum bar height % when value is 0 or very small
const DOT_GAP_PX = 10; // visual gap between dot and bar

export interface PlSummaryCardProps {
  totalAmount: string;
  changePercent: string;
  changeLabel?: string;
  revenueAmount: string;
  expensesAmount: string;
  ebitdaAmount: string;
  /** Optional raw values for dynamic bar heights; when omitted, fallback heights are used */
  revenueValue?: number;
  expenseValue?: number;
  ebitdaValue?: number;
  /** When true, renders PlSummaryCardSkeleton instead of content */
  loading?: boolean;
}

export function PlSummaryCard({
  totalAmount,
  changePercent,
  changeLabel = "Compare from last month",
  revenueAmount,
  expensesAmount,
  ebitdaAmount,
  revenueValue,
  expenseValue,
  ebitdaValue,
  loading = false,
}: PlSummaryCardProps) {
  const barHeights = useMemo(() => {
    const hasValues =
      typeof revenueValue === "number" &&
      typeof expenseValue === "number" &&
      typeof ebitdaValue === "number";
    const maxAbs = hasValues
      ? Math.max(
          Math.abs(revenueValue ?? 0),
          Math.abs(expenseValue ?? 0),
          Math.abs(ebitdaValue ?? 0),
          Number.EPSILON,
        )
      : 1;
    const pct = (v: number) => {
      const ratio = maxAbs > 0 ? (Math.abs(v) / maxAbs) * 100 : 0;
      return Math.max(ratio, MIN_BAR_PERCENT);
    };
    return {
      revenue: hasValues ? pct(revenueValue!) : 75,
      expense: hasValues ? pct(expenseValue!) : 50,
      ebitda: hasValues ? pct(ebitdaValue!) : 21,
    };
  }, [revenueValue, expenseValue, ebitdaValue]);

  if (loading) {
    return <PlSummaryCardSkeleton />;
  }

  const calcBottom = (pct: number) => `calc(${pct}% + ${DOT_GAP_PX}px)`;
  const isNegativeChange = changePercent.trim().startsWith("-");
  const isEmptyChange =
    changePercent.trim() === "" || changePercent.trim() === "—";
  const showDown = !isEmptyChange && isNegativeChange;
  const TrendIcon = showDown ? FeatherArrowDown : FeatherArrowUp;
  const trendPillClass = showDown ? "bg-error-50" : "bg-success-50";
  const trendIconClass = showDown ? "text-error-600" : "text-success-700";

  return (
    <div className="flex min-h-0 flex-1 min-w-0 flex-col items-start gap-6 rounded-2xl bg-white px-6 py-7 shadow-lg self-stretch sm:min-h-[22rem]">
      <div className="flex items-center gap-2">
        <span className="text-heading-3 font-heading-3 text-default-font">
          P&L Summary
        </span>
        <div className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-neutral-100">
          <FeatherInfo className="text-caption font-caption text-neutral-400" />
        </div>
      </div>
      <div className="flex flex-col items-start gap-2">
        <div className="flex gap-4 items-baseline">
          <span className="font-['Inter'] text-[36px] font-[700] leading-[40px] text-neutral-900">
            {totalAmount}
          </span>
          <div
            className={`flex items-center gap-1 rounded-full px-2 py-1 ${trendPillClass}`}
          >
            <TrendIcon
              className={`text-caption font-caption ${trendIconClass}`}
            />
            <span
              className={`text-caption-bold font-caption-bold ${trendIconClass}`}
            >
              {changePercent}
            </span>
          </div>
        </div>
        <span className="text-body font-body text-neutral-400">
          {changeLabel}
        </span>
      </div>
      <div className="mt-1 flex w-full flex-1 flex-col items-start justify-end gap-4 pt-5">
        <div className="flex w-full items-stretch gap-3">
          <div className="flex flex-col items-start gap-1.5 flex-1 relative">
            <div className="relative flex w-full min-h-[128px] items-stretch">
              <div
                className="absolute left-0 w-0.5 bg-success-500 min-h-[4px] opacity-100"
                style={{ height: `${barHeights.revenue}%`, bottom: 0 }}
              />
              <div
                className="absolute left-0 flex items-center gap-2"
                style={{ bottom: calcBottom(barHeights.revenue) }}
              >
                <div className="h-2 w-2 rounded-full bg-success-500" />
                <div className="flex items-center gap-1 rounded-full border border-solid border-success-200 bg-success-50 px-3 py-1">
                  <span className="text-caption-bold font-caption-bold text-success-700 whitespace-nowrap">
                    {revenueAmount}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex h-2 w-full flex-none flex-col items-start rounded-full bg-success-500" />
            <span className="text-body-bold font-body-bold text-neutral-700">
              Revenue
            </span>
          </div>
          <div className="flex flex-col items-start gap-1.5 flex-1 relative">
            <div className="relative flex w-full min-h-[128px] items-stretch">
              <div
                className="absolute left-0 w-0.5 bg-warning-500 min-h-[4px] opacity-100"
                style={{ height: `${barHeights.expense}%`, bottom: 0 }}
              />
              <div
                className="absolute left-0 flex items-center gap-2"
                style={{ bottom: calcBottom(barHeights.expense) }}
              >
                <div className="h-2 w-2 rounded-full bg-warning-500" />
                <div className="flex items-center gap-1 rounded-full border border-solid border-warning-200 bg-warning-50 px-3 py-1">
                  <span className="text-caption-bold font-caption-bold text-warning-700 whitespace-nowrap">
                    {expensesAmount}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex h-2 w-full flex-none flex-col items-start rounded-full bg-warning-500" />
            <span className="text-body-bold font-body-bold text-neutral-700">
              Expenses
            </span>
          </div>
          <div className="flex flex-col items-start gap-1.5 flex-1 relative">
            <div className="relative flex w-full min-h-[128px] items-stretch">
              <div
                className="absolute left-0 w-0.5 bg-brand-500 min-h-[4px] opacity-100"
                style={{ height: `${barHeights.ebitda}%`, bottom: 0 }}
              />
              <div
                className="absolute left-0 flex items-center gap-2"
                style={{ bottom: calcBottom(barHeights.ebitda) }}
              >
                <div className="h-2 w-2 rounded-full bg-brand-500" />
                <div className="flex items-center gap-1 rounded-full border border-solid border-brand-200 bg-brand-50 px-3 py-1">
                  <span className="text-caption-bold font-caption-bold text-brand-700 whitespace-nowrap">
                    {ebitdaAmount}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex h-2 w-full flex-none flex-col items-start rounded-full bg-brand-500" />
            <span className="text-body-bold font-body-bold text-neutral-700">
              EBITDA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
